import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import Header from '../components/Header';

const Register = () => {
    const { register, login } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const [userData, setUserData] = useState({
        name: '', last_name: '', email: '', identification: '', city: '', country: 'Colombia'
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        
        // Map identification to email if disabled
        const finalData = { ...userData };
        if (config?.registration_fields?.identification === false) {
            finalData.identification = userData.email;
        }

        const result = await register(finalData);
        if (result.success) {
            await login(finalData.identification);
            navigate('/');
        } else {
            setError(result.message);
            setIsLoading(false);
        }
    };

    if (configLoading) {
        return (
            <div className="min-h-screen bg-[#1c0042] flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
            </div>
        );
    }

    const reg = config?.registration_fields || {};
    
    // Helper to determine if a field should be full width
    const getColSpan = (field, partner) => {
        if (reg[partner] === false) return "col-span-2";
        return "col-span-1";
    };

    return (
        <div className="min-h-screen bg-[#1c0042] flex flex-col items-center justify-center p-6 sm:p-10 animate-fadeIn overflow-y-auto bg-gradient-to-b from-[#1c0042] to-[#0e0024]">
            <Header title="Registro" subtitle="NeuroUpdate 2026" />

            <div className="w-full max-w-sm z-10 space-y-8 bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-[40px] shadow-2xl">
                <div className="text-center">
                    <h2 className="text-2xl font-black text-white tracking-tight uppercase italic">Registro</h2>
                    <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.3em] mt-1">NeuroUpdate 2026</p>
                </div>

                <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                    {/* Identification */}
                    {reg.identification !== false && (
                        <div className="col-span-2 space-y-1">
                            <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Identificación</label>
                            <input 
                                className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                placeholder="Cédula"
                                value={userData.identification}
                                onChange={(e) => setUserData({...userData, identification: e.target.value})}
                                required
                            />
                        </div>
                    )}
                    
                    {/* Name */}
                    {reg.name !== false && (
                        <div className={`${getColSpan('name', 'last_name')} space-y-1`}>
                            <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Nombres</label>
                            <input 
                                className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                value={userData.name}
                                onChange={(e) => setUserData({...userData, name: e.target.value})}
                                required
                            />
                        </div>
                    )}

                    {/* Last Name */}
                    {reg.last_name !== false && (
                        <div className={`${getColSpan('last_name', 'name')} space-y-1`}>
                            <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Apellidos</label>
                            <input 
                                className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                value={userData.last_name}
                                onChange={(e) => setUserData({...userData, last_name: e.target.value})}
                                required
                            />
                        </div>
                    )}

                    {/* Email - Always full width */}
                    <div className="col-span-2 space-y-1">
                        <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Email</label>
                        <input 
                            type="email"
                            className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                            value={userData.email}
                            onChange={(e) => setUserData({...userData, email: e.target.value})}
                            required
                        />
                    </div>

                    {/* City */}
                    {reg.city !== false && (
                        <div className={`${getColSpan('city', 'country')} space-y-1`}>
                            <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Ciudad</label>
                            <input 
                                className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                value={userData.city}
                                onChange={(e) => setUserData({...userData, city: e.target.value})}
                                required
                            />
                        </div>
                    )}

                    {/* Country */}
                    {reg.country !== false && (
                        <div className={`${getColSpan('country', 'city')} space-y-1`}>
                            <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">País</label>
                            <input 
                                className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                value={userData.country}
                                onChange={(e) => setUserData({...userData, country: e.target.value})}
                                required
                            />
                        </div>
                    )}

                    <div className="col-span-2 pt-4">
                        {error && (
                            <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-[9px] font-black uppercase tracking-widest border border-red-500/30 text-center mb-4">
                                {error}
                            </div>
                        )}

                        <button 
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-white text-[#1c0042] py-4 rounded-3xl font-black uppercase tracking-widest text-[11px] shadow-2xl transition-all transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
                        >
                            {isLoading ? 'PROCESANDO...' : 'FINALIZAR REGISTRO'}
                        </button>
                        
                        <div className="text-center pt-4">
                            <Link to="/login" className="text-[10px] font-black text-white/40 uppercase tracking-widest hover:text-white transition-colors">
                                ← Volver al Ingreso
                            </Link>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Register;
